'use client';

import { useState, FormEvent } from 'react';

import Footer from '@/components/common/Footer';
import Header from '@/components/common/Header';
import { trackDiscoveryCallClick } from '@/lib/analytics';
import { getUserContext } from '@/lib/userContext';

interface FormData {
  fullName: string;
  email: string;
  message: string;
  company_website: string; // honeypot
}

interface FormErrors {
  fullName?: string;
  email?: string;
  message?: string;
  submit?: string;
}

export default function ContactPage() {
  const [formData, setFormData] = useState<FormData>({
    fullName: '',
    email: '',
    message: '',
    company_website: '', // honeypot
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.fullName.trim()) {
      newErrors.fullName = 'Full name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setErrors({});
    setSubmitSuccess(false);

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 429) {
          setErrors({ submit: data.error || 'Too many requests. Please try again later.' });
        } else {
          setErrors({ submit: data.error || 'Failed to send message. Please try again.' });
        }
        return;
      }

      setSubmitSuccess(true);
      setFormData({
        fullName: '',
        email: '',
        message: '',
        company_website: '',
      });
    } catch (error) {
      setErrors({ submit: 'Network error. Please check your connection and try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name as keyof FormErrors]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  return (
    <>
      <Header />
      <div className="min-h-screen flex flex-col">
        <main className="flex-grow">
          {/* Hero Section - Flat Background #2c0f3b, Reduced Height 20-25%, Single CTA */}
          <section className="w-full bg-[#2c0f3b] py-8 sm:py-10 lg:py-12">
            <div className="w-full max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-20 xl:px-36">
              <div className="text-center max-w-[900px] mx-auto">
                <h1 className="text-[32px] sm:text-[40px] lg:text-[48px] font-bebas font-normal leading-[1.15] text-white mb-3">
                  A dedicated team for your digital work
                </h1>
                <p className="text-[15px] sm:text-[16px] lg:text-[17px] font-poppins font-normal leading-[1.5] text-[#e8e8e8] mb-5">
                  Websites, marketing, and video handled for one flat monthly fee.
                </p>
                {/* Single Primary CTA Button - Reduced Dominance */}
                <div className="flex flex-col items-center">
                  <a
                    href="https://calendly.com/john-smulo/purple-cow-unlimited-discovery-call"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => {
                      getUserContext().then(context => {
                        const segment = context?.role || 'unauthenticated';
                        trackDiscoveryCallClick(
                          segment === 'super_admin' ? 'super_admin' : 
                          segment === 'ops_admin' || segment === 'admin' ? 'admin' : 
                          segment === 'team' ? 'team' : 
                          segment === 'client' ? 'client' : 'unauthenticated',
                          '/contact',
                          'contact_page'
                        );
                      });
                    }}
                    className="inline-block bg-white text-[#8923b3] hover:bg-[#f8f6fa] font-poppins font-semibold text-[15px] px-7 py-3 rounded-full transition-all duration-200 shadow-md hover:shadow-lg"
                  >
                    Schedule a Discovery Call
                  </a>
                </div>
              </div>
            </div>
          </section>

          {/* Form Section - Constrained Width, Tighter Layout */}
          <section className="w-full bg-white py-16 sm:py-20 lg:py-24">
            <div className="w-full max-w-[1100px] mx-auto px-4 sm:px-6 lg:px-8">
              {submitSuccess ? (
                <div className="max-w-[700px] mx-auto text-center py-16">
                  <div className="mb-10">
                    <svg className="w-24 h-24 text-green-500 mx-auto mb-8" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <h2 className="text-[24px] sm:text-[28px] font-poppins font-semibold text-[#1a1a1a] mb-4">
                      Thank you for reaching out!
                    </h2>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-[1.5fr_1fr] gap-8 lg:gap-12">
                  {/* Left Column - Form (Primary Focus) */}
                  <div className="order-2 lg:order-1">
                    <div className="mb-6">
                      <h2 className="text-[28px] sm:text-[32px] lg:text-[36px] font-bebas font-normal text-[#3f1651] mb-2">
                        Get in touch
                      </h2>
                      <p className="text-[15px] sm:text-[16px] font-poppins text-[#555555] leading-[1.6]">
                        Tell us what you're working on and where you need support.
                      </p>
                    </div>

                    {errors.submit && (
                      <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                        <p className="text-red-800 font-poppins text-[14px]">
                          {errors.submit}
                        </p>
                      </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-5">
                      {/* Full Name */}
                      <div>
                        <label htmlFor="fullName" className="block text-[14px] font-poppins font-medium text-[#1a1a1a] mb-1.5">
                          Full name
                        </label>
                        <input
                          type="text"
                          id="fullName"
                          name="fullName"
                          value={formData.fullName}
                          onChange={handleChange}
                          className={`w-full px-4 py-2.5 border rounded-lg font-poppins text-[15px] focus:outline-none focus:ring-2 focus:ring-[#8923b3] transition-all ${
                            errors.fullName ? 'border-red-500' : 'border-gray-300'
                          }`}
                          placeholder="Your name"
                        />
                        {errors.fullName && (
                          <p className="mt-2 text-red-500 text-[13px] font-poppins">{errors.fullName}</p>
                        )}
                      </div>

                      {/* Email Address */}
                      <div>
                        <label htmlFor="email" className="block text-[14px] font-poppins font-medium text-[#1a1a1a] mb-1.5">
                          Email address
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          className={`w-full px-4 py-2.5 border rounded-lg font-poppins text-[15px] focus:outline-none focus:ring-2 focus:ring-[#8923b3] transition-all ${
                            errors.email ? 'border-red-500' : 'border-gray-300'
                          }`}
                          placeholder="Your email"
                        />
                        {errors.email && (
                          <p className="mt-2 text-red-500 text-[13px] font-poppins">{errors.email}</p>
                        )}
                      </div>

                      {/* Message */}
                      <div>
                        <label htmlFor="message" className="block text-[14px] font-poppins font-medium text-[#1a1a1a] mb-1.5">
                          Message
                        </label>
                        <textarea
                          id="message"
                          name="message"
                          value={formData.message}
                          onChange={handleChange}
                          rows={6}
                          className={`w-full px-4 py-2.5 border rounded-lg font-poppins text-[15px] focus:outline-none focus:ring-2 focus:ring-[#8923b3] resize-none transition-all ${
                            errors.message ? 'border-red-500' : 'border-gray-300'
                          }`}
                          placeholder="Briefly describe what you want help with"
                        />
                        {errors.message && (
                          <p className="mt-2 text-red-500 text-[13px] font-poppins">{errors.message}</p>
                        )}
                      </div>

                      {/* Honeypot field - hidden from users */}
                      <div className="hidden">
                        <input
                          type="text"
                          name="company_website"
                          value={formData.company_website}
                          onChange={handleChange}
                          tabIndex={-1}
                          autoComplete="off"
                        />
                      </div>

                      {/* Submit Button */}
                      <div className="pt-2">
                        <button
                          type="submit"
                          disabled={isSubmitting}
                          className="w-full bg-[#8923b3] hover:bg-[#7a1f9e] text-white font-poppins font-semibold text-[16px] px-6 py-3.5 rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-md hover:shadow-lg"
                        >
                          {isSubmitting ? 'Sending...' : 'Send message'}
                        </button>
                      </div>
                    </form>
                  </div>

                  {/* Right Column - Reassurance Panel (Reduced Contrast) */}
                  <div className="order-1 lg:order-2">
                    <div className="bg-[#f9f8fa] border border-gray-100 rounded-xl p-6 sm:p-7">
                      <h3 className="text-[19px] sm:text-[20px] font-poppins font-semibold text-[#3f1651] mb-4">
                        What to expect
                      </h3>
                      <ul className="space-y-3.5">
                        <li className="flex items-start gap-3">
                          <div className="w-1.5 h-1.5 rounded-full bg-[#8923b3] flex-shrink-0 mt-2"></div>
                          <span className="text-[14px] font-poppins text-[#666666] leading-[1.6]">
                            Your message is reviewed directly
                          </span>
                        </li>
                        <li className="flex items-start gap-3">
                          <div className="w-1.5 h-1.5 rounded-full bg-[#8923b3] flex-shrink-0 mt-2"></div>
                          <span className="text-[14px] font-poppins text-[#666666] leading-[1.6]">
                            You'll get a clear, thoughtful reply
                          </span>
                        </li>
                        <li className="flex items-start gap-3">
                          <div className="w-1.5 h-1.5 rounded-full bg-[#8923b3] flex-shrink-0 mt-2"></div>
                          <span className="text-[14px] font-poppins text-[#666666] leading-[1.6]">
                            No pressure or scripts
                          </span>
                        </li>
                        <li className="flex items-start gap-3">
                          <div className="w-1.5 h-1.5 rounded-full bg-[#8923b3] flex-shrink-0 mt-2"></div>
                          <span className="text-[14px] font-poppins text-[#666666] leading-[1.6]">
                            You'll speak with a real person
                          </span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
}